/**
 * Domain model allineato allo Swagger "Software Updates API" v1.0.0.
 * basePath: /api/updates
 *
 * Naming: *Response = body di risposta; CreateOrUpdate*Request = body write.
 * Alias brevi (Product, Hardware, …) mantenuti per il modulo software-updates.
 *
 * Tipi entità e wrapper di risposta API: src/app/model/api/data-rest.ts
 */

import type {
	CompatibilityRuleData,
	CompatibilityRuleItemData,
	CompatibilityRuleTypesData,
	ComponentData,
	DownloadSoftwareReleaseData,
	HardwareData,
	ProductData,
	ReleaseNoteData,
	SoftwareReleaseData,
	SoftwareReleaseSubComponentData,
} from 'src/app/model/api/data-rest';

export type {
	AuditableResponse,
	CompatibilityRuleData,
	CompatibilityRuleItemData,
	CompatibilityRuleTypesData,
	ComponentData as ComponentResponse,
	DownloadSoftwareReleaseData as DownloadSoftwareReleaseResponse,
	HardwareData as HardwareResponse,
	ProductData as ProductResponse,
	ReleaseNoteData as ReleaseNoteResponse,
	SoftwareReleaseData as SoftwareReleaseResponse,
} from 'src/app/model/api/data-rest';

// ---------------------------------------------------------------------------
// Domain literals
// ---------------------------------------------------------------------------

export const FIRMWARE_PRODUCT_TYPES = ['device', 'board', 'tool', 'app'] as const;
export type FirmwareProductType = typeof FIRMWARE_PRODUCT_TYPES[number];
export type ProductType = FirmwareProductType;

/** Tipi prodotto con gerarchia hardware → componenti → release (come i dispositivi). */
export const HARDWARE_BASED_FIRMWARE_PRODUCT_TYPES = ['device', 'board'] as const;
export type HardwareBasedFirmwareProductType = typeof HARDWARE_BASED_FIRMWARE_PRODUCT_TYPES[number];

export function isHardwareBasedFirmwareProductType(
	type?: FirmwareProductType | string | null,
): boolean {
	return type != null && (HARDWARE_BASED_FIRMWARE_PRODUCT_TYPES as readonly string[]).includes(type);
}

export const FIRMWARE_RSE_MANDATORY_VALUES = ['Y', 'N'] as const;
export type FirmwareRseMandatory = typeof FIRMWARE_RSE_MANDATORY_VALUES[number];

export enum EFirmwareProductType {
	DEVICE = 'device',
	BOARD = 'board',
	TOOL = 'tool',
	APP = 'app',
}

export enum EFirmwareRseMandatory {
	Yes = 'Y',
	No = 'N',
}

// ---------------------------------------------------------------------------
// Product — definitions/ProductResponse, CreateOrUpdateProductRequest
// ---------------------------------------------------------------------------

export type Product = ProductData;
export type FirmwareProduct = ProductData;

export interface CreateOrUpdateProductRequest {
	name?: string;
	type?: FirmwareProductType | string;
	/** Usato in lettura risposta API. In scrittura preferire deviceModelId. */
	modelId?: number;
	/** Id scheda (tipo board), codificato come BoardClass * 1000 + BoardId. */
	crpClassName?: string;
	deviceModelId?: number;
	enabled?: boolean;
	rseMandatory?: FirmwareRseMandatory | string;
}

// ---------------------------------------------------------------------------
// Hardware — definitions/HardwareResponse, AddOrUpdateHardwareRequest
// ---------------------------------------------------------------------------

export type Hardware = HardwareData;
export type FirmwareHardware = HardwareData;

export interface AddOrUpdateHardwareRequest {
	id?: number;
	productId?: number;
	version?: string;
}

// ---------------------------------------------------------------------------
// Component — definitions/ComponentResponse, AddOrUpdateComponentRequest
// ---------------------------------------------------------------------------

export type SoftwareComponent = ComponentData;
export type FirmwareSoftwareComponent = ComponentData;

export interface AddOrUpdateComponentRequest {
	id?: number;
	hardwareId?: number;
	name?: string;
}

// ---------------------------------------------------------------------------
// Software release — definitions/SoftwareReleaseResponse, CreateOrUpdateSoftwareReleaseRequest
// ---------------------------------------------------------------------------

export interface SoftwareReleaseTarget {
	productId?: number;
	hardwareId?: number;
	componentId?: number;
}

export type FirmwareReleaseTarget = SoftwareReleaseTarget;
export type SoftwareReleaseTargetRequest = SoftwareReleaseTarget;

export type SoftwareReleaseSubComponent = SoftwareReleaseSubComponentData;
export type FirmwareReleaseSubComponent = SoftwareReleaseSubComponent;

export type SoftwareRelease = SoftwareReleaseData;
export type FirmwareSoftwareRelease = SoftwareReleaseData;

export interface CreateOrUpdateSoftwareReleaseRequest {
	version: string;
	target: SoftwareReleaseTarget;
	byteSize?: number;
	checksum?: string;
	s3Filename?: string;
	isComposite?: boolean;
	isEncrypted?: boolean;
	isLocalUpdate?: boolean;
	isMandatory?: boolean;
	isPublic?: boolean;
	isPublished?: boolean;
	isSecurityUpdate?: boolean;
	isSigned?: boolean;
	isTestLegacy?: boolean;
	publicUrl?: string;
	subComponents?: SoftwareReleaseSubComponent[];
}

export type CreateFirmwareReleaseRequest = CreateOrUpdateSoftwareReleaseRequest;
export type CreateReleaseRequest = CreateOrUpdateSoftwareReleaseRequest;
export type UpdateFirmwareReleaseRequest = CreateOrUpdateSoftwareReleaseRequest;
export type UpdateReleaseRequest = CreateOrUpdateSoftwareReleaseRequest;

export interface PublishSoftwareReleaseRequest {
	isPublished: boolean;
	releasedAt?: string;
}

// ---------------------------------------------------------------------------
// Release note — definitions/ReleaseNoteResponse, CreateOrUpdateReleaseNoteRequest
// ---------------------------------------------------------------------------

export type ReleaseNote = ReleaseNoteData;
export type FirmwareReleaseNote = ReleaseNoteData;

export interface CreateOrUpdateReleaseNoteRequest {
	language: string;
	note?: string;
}

export type FirmwareReleaseNoteRequest = CreateOrUpdateReleaseNoteRequest;
export type ReleaseNoteRequest = CreateOrUpdateReleaseNoteRequest;

// ---------------------------------------------------------------------------
// Compatibility rule — definitions/CompatibilityRuleResponse, Create/UpdateCompatibilityRuleRequest
// ---------------------------------------------------------------------------

export type CompatibilityRule = CompatibilityRuleData;
export type FirmwareCompatibilityRule = CompatibilityRuleData;
export type CompatibilityRuleResponse = CompatibilityRuleData;

export type CompatibilityRuleItem = CompatibilityRuleItemData;
export type CompatibilityRuleType = CompatibilityRuleItemData;
export type CompatibilityRuleTypeResponse = CompatibilityRuleItemData;
export type FirmwareCompatibilityRuleType = CompatibilityRuleItemData;
export type CompatibilityRuleTypes = CompatibilityRuleTypesData;
export type CompatibilityRuleTypesResponse = CompatibilityRuleTypesData;

export interface CreateCompatibilityRuleRequest {
	type?: string;
	operator?: string;
	value: string[];
}

export interface UpdateCompatibilityRuleRequest {
	type?: string;
	operator?: string;
	value?: string[];
}

/** @deprecated Use CreateCompatibilityRuleRequest or UpdateCompatibilityRuleRequest */
export type CreateOrUpdateCompatibilityRuleRequest = UpdateCompatibilityRuleRequest;

export type CompatibilityRuleRequest = CreateCompatibilityRuleRequest;
export type FirmwareCompatibilityRuleRequest = CreateCompatibilityRuleRequest;

// ---------------------------------------------------------------------------
// Client download — definitions/DownloadSoftwareReleaseResponse
// ---------------------------------------------------------------------------

export type DownloadSoftwareRelease = DownloadSoftwareReleaseData;
export type FirmwareDownloadSoftwareRelease = DownloadSoftwareReleaseData;

// ---------------------------------------------------------------------------
// Query params (client & management release list)
// ---------------------------------------------------------------------------

/** GET /v1/client/release, GET /v1/client/release/latest */
export interface ClientReleaseQueryParams {
	deviceId?: number;
	keycode?: string;
	serial?: string;
	name?: string;
}

/** GET /v1/management/release — almeno un filtro obbligatorio. */
export interface ReleaseListQueryParams {
	installationTargetId?: number;
	productId?: number;
	hardwareId?: number;
	componentId?: number;
	offset?: number;
	limit?: number;
	version?: string;
}

/** Limite per il fetch bulk delle release per componente (senza paginazione UI).
 * Impostato a 50 perchè è il valore massimo impostato attualmente a backend
 */
export const COMPONENT_RELEASES_FETCH_LIMIT = 50;

// ---------------------------------------------------------------------------
// UI helpers
// ---------------------------------------------------------------------------

export const FIRMWARE_UPDATES_API_BASE_PATH = '/api/updates';

export const FIRMWARE_RELEASE_DEFAULT_PAGE_SIZE = 10;

export const FIRMWARE_PRODUCT_TYPE_I18N_KEYS: Record<FirmwareProductType, string> = {
	device: 'DEVICE',
	board: 'SOFTWARE_UPDATE_TYPE_BOARD',
	tool: 'SOFTWARE_UPDATE_TYPE_TOOL',
	app: 'SOFTWARE_UPDATE_TYPE_APP',
};

export const FIRMWARE_PRODUCT_TYPE_TAG_STYLES: Record<FirmwareProductType, string> = {
	device: 'text-0 p-tag-lightBlue0260',
	board: 'text-0 p-tag-lightBlue0280',
	app: 'text-0 p-tag-lightBlue0240',
	tool: 'text-0 p-tag-lightBlue0220',
};
